<script setup>
/* Reiterleiste des Profils (Player Info, Documents, Trials, Material, X11 Motion …) in der Art der Leiste der
   Startseite: eine Zeile, bei Platzmangel waagerecht scrollbar (nie zweireihig), der aktive Reiter als Kapsel,
   die übrigen gedämpft. Kein umschließender Rahmen, kein Akzent-Text im aktiven Reiter.

   Die Leiste gehört NICHT zur bewegten Karte (PlayerPanel.vue: card-slide) und steht beim Spielerwechsel fest.
   Welche Reiter es gibt und ob sie gesperrt sind, entscheidet das Panel (domain/playerTabs.js + Rechte); hier
   nur Darstellung und Bedienung.

   Der Kapselhintergrund (.tab-slider) gleitet hinter den aktiven Reiter; Position und Maße kommen aus den echten
   Elementen (unterschiedlich lange Beschriftungen) und berücksichtigen das waagerechte Scrollen der Leiste. Wird
   der aktive Reiter durch Scrollen verdeckt, scrollt die Leiste ihn ins Bild.

   Bedienung: Klick, sowie Enter/Leertaste bei Fokus. Rolle tablist/tab mit aria-selected. Mindesthöhe 44 px.
   Pfeiltasten gehören der Leiste nicht (der Spielerwechsel ignoriert Ereignisse aus role="tablist"). */
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  /* [{ key, label, allowed }] — Label ist ein i18n-Schlüssel */
  tabs: { type: Array, required: true },
  activeKey: { type: String, required: true },
});
const emit = defineEmits(['select']);

const tabsEl = ref(null);
const tabEls = ref({});
const sliderStyle = ref({ transform: 'translate(0, 0)', width: '0px', height: '0px', opacity: 0 });

function registerTabEl(el, key) {
  if (el) tabEls.value[key] = el;
  else delete tabEls.value[key];
}

const EDGE_PX = 16;

function updateSlider() {
  const container = tabsEl.value;
  const active = tabEls.value[props.activeKey];
  if (!container || !active) return;
  /* Erst ins Bild scrollen, dann messen */
  let containerRect = container.getBoundingClientRect();
  let rect = active.getBoundingClientRect();
  const left = rect.left - containerRect.left;
  if (left < 0) container.scrollLeft += left - EDGE_PX;
  else if (left + rect.width > containerRect.width) container.scrollLeft += left + rect.width - containerRect.width + EDGE_PX;
  containerRect = container.getBoundingClientRect();
  rect = active.getBoundingClientRect();
  sliderStyle.value = {
    transform: `translate(${rect.left - containerRect.left + (container.scrollLeft || 0)}px, ${rect.top - containerRect.top}px)`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    opacity: 1,
  };
}

watch(() => [props.activeKey, props.tabs], () => nextTick(updateSlider), { flush: 'post' });
onMounted(() => {
  window.addEventListener('resize', updateSlider);
  nextTick(updateSlider);
});
onBeforeUnmount(() => window.removeEventListener('resize', updateSlider));
</script>

<template>
  <div ref="tabsEl" class="tabs" role="tablist">
    <div class="tab-slider" :style="sliderStyle"></div>
    <div
      v-for="t in tabs"
      :key="t.key"
      :ref="(el) => registerTabEl(el, t.key)"
      class="tab"
      :class="{ active: activeKey === t.key, locked: !t.allowed }"
      role="tab"
      tabindex="0"
      :aria-selected="activeKey === t.key"
      @click="emit('select', t.key)"
      @keydown.enter.prevent="emit('select', t.key)"
      @keydown.space.prevent="emit('select', t.key)"
    >
      {{ $t(t.label).toUpperCase() }}
      <svg
        v-if="!t.allowed"
        class="tab-lock"
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
      >
        <rect x="4" y="10.5" width="16" height="10" rx="2" />
        <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" stroke-linecap="round" />
      </svg>
    </div>
  </div>
</template>

<style scoped>
/* Eine Zeile, waagerecht scrollbar, ohne Rahmen. Die Leiste klebt oben im scrollenden Inhalt und deckt
   darunter durchlaufenden Inhalt leicht ab. */
.tabs {
  position: sticky;
  top: 0;
  /* Über allem, was darunter scrollt (Kartenvorschau mit Maske, Videos). Eigener Stapelkontext, damit ein Nachbar
     mit Maske oder Transform (WebKit legt solche Elemente auf eine eigene Ebene) nie darüber malt. */
  z-index: 10;
  isolation: isolate;
  display: flex;
  flex-wrap: nowrap;
  gap: 2px;
  margin: 0 0 16px;
  padding: 4px 0;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  /* Deckender Hintergrund (Alpha 1) und eine feine Linie unten: Der Inhalt scheint nie durch die feste Leiste. Die
     Leiste reicht über die Seitenränder des scrollenden Rahmens hinaus, damit auch dort nichts durchscheint. */
  background: var(--bg);
  box-shadow: 0 1px 0 var(--border);
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.tabs::-webkit-scrollbar {
  display: none;
}
.tab {
  position: relative;
  z-index: 1;
  flex: none;
  min-width: 0;
  /* Sichtbare Kapsel 36 px; die Klickfläche reicht per ::after unsichtbar auf 44 px (4 px oben und unten, die
     Leiste hat dafür 4 px Polster; nebeneinander überlappen die Flächen nicht). */
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 14px;
  border-radius: 999px;
  font-family: var(--display);
  font-size: var(--pv-tab, 13px);
  font-weight: 500;
  letter-spacing: 0.04em;
  white-space: nowrap;
  color: var(--muted);
  cursor: pointer;
  transition: color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}
.tab::after {
  content: '';
  position: absolute;
  inset: -4px 0;
}
.tab:hover {
  color: var(--text);
}
/* Aktiver Reiter: Kapsel und helle Schrift, nicht mehr Gewicht */
.tab.active {
  color: var(--text);
  /* main.css setzt für .tab.active 600: hier ausdrücklich 500 (Rang durch Kapsel und Helligkeit, nicht Gewicht) */
  font-weight: 500;
}
.tab:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: -2px;
}
/* Kapsel hinter dem aktiven Reiter */
.tab-slider {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  border-radius: 999px;
  background: var(--card-hover);
  opacity: 0;
  transition: transform 0.25s ease, width 0.25s ease, height 0.25s ease, opacity 0.15s ease;
}
/* Gesperrte Reiter bleiben anklickbar — sie erklären dann, warum sie verschlossen sind. */
.tab.locked {
  color: var(--dim);
}
.tab.locked.active {
  color: var(--muted);
}
.tab-lock {
  vertical-align: -1px;
  opacity: 0.75;
}
@media (prefers-reduced-motion: reduce) {
  .tab,
  .tab-slider {
    transition: none;
  }
}
</style>
